import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import * as _ from "@antv/util";
import { Button, Col, Flex, Row } from "antd";
import Decimal from "decimal.js";
import { useCallback, useContext, useEffect, useState } from "react";
import { IndexContext } from "../../context/IndexContext";
import SpecialCategoryModal from "./SpecialCategoryModal";

const PRICE_SELECTION = [0.1, 0.5, 1.0];
const CATEGORY_TO_FILTER = "Noodles";
const NOODLE_TYPE_TO_DISPLAY = "Style";

export default function CorrectionTab({
  selectedItemKey,
  setSelectedItemKey,
  plates,
  setPlates,
  selectedPlateIndex,
  category,
  selectedSubCategory,
  setSelectedSubCategory,
}) {
  const { isLightTheme } = useContext(IndexContext);

  const [stagedPlates, setStagedPlates] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState({
    label: null,
    sub_categories: [],
    modal_sub_categories: [],
  });

  const structuredClone = (obj) => JSON.parse(JSON.stringify(obj));

  useEffect(() => {
    setStagedPlates(structuredClone(plates));
  }, [plates, selectedPlateIndex]);

  useEffect(() => {
    const item = stagedPlates[selectedPlateIndex]?.items.find(
      (item) => item.key === selectedItemKey
    );
    if (!selectedItemKey || !item) {
      setCurrentCategory({
        label: null,
        sub_categories: [],
        modal_sub_categories: [],
      });
      return;
    }

    applySubCategories(item.category);
  }, [selectedItemKey, stagedPlates, selectedPlateIndex, category]);

  useEffect(() => {
    if (!selectedItemKey) return;

    const itemExists = stagedPlates[selectedPlateIndex]?.items.some(
      (item) => item.key === selectedItemKey
    );
    if (!itemExists) setSelectedItemKey(null);
  }, [selectedItemKey, stagedPlates, selectedPlateIndex, setSelectedItemKey]);

  useEffect(() => {
    if (!selectedItemKey) {
      setStagedPlates(structuredClone(plates));
    }
  }, [selectedItemKey, plates]);

  const applySubCategories = (selectedCategory) => {
    const category_data = category.find(
      (cat) => cat.label === selectedCategory
    );
    if (!category_data) {
      setCurrentCategory({
        label: selectedCategory,
        sub_categories: [],
        modal_sub_categories: [],
      });
      return;
    }

    const subCategories = category_data.children || [];
    const modalSubCategories = category_data.options || [];

    setCurrentCategory({
      label: selectedCategory,
      sub_categories: subCategories,
      modal_sub_categories: modalSubCategories,
    });
  };

  const addOrUpdateItem = (cat) => {
    const updated_plates = structuredClone(stagedPlates);
    const plate = updated_plates[selectedPlateIndex];
    if (!plate) return;

    const selectedPlate = plate.items.find(
      (item) => item.key === selectedItemKey
    );

    if (selectedPlate) {
      Object.assign(selectedPlate, {
        category_id: cat.id,
        category: cat.label,
        sub_category: cat.label,
        unit_price: cat.unit_price,
        quantity: 1,
      });
    } else {
      const new_item_key = _.uniqueId("item-");
      const new_item = {
        key: new_item_key,
        category_id: cat.default_child_category_id || cat.id,
        category: cat.label,
        sub_category: cat.default_child_category_name || "",
        unit_price: cat.default_child_unit_price || cat.unit_price,
        quantity: 1,
      };
      plate.items.push(new_item);
      setSelectedItemKey(new_item_key);
    }

    setSelectedSubCategory(cat.default_child_category_name || "");
    setStagedPlates(updated_plates);
  };

  function onSubCatButtonClicked(sub_cat) {
    if (!selectedItemKey) return;

    const cloned_plates = structuredClone(stagedPlates);
    const item = cloned_plates[selectedPlateIndex]?.items.find(
      (item) => item.key === selectedItemKey
    );
    if (!item) return;

    Object.assign(item, {
      category_id: sub_cat.id,
      sub_category: sub_cat.label,
      unit_price: sub_cat.unit_price,
    });

    setStagedPlates(cloned_plates);
    setSelectedSubCategory(sub_cat.label);
  }

  function handlePrice(add_value, is_plus) {
    if (!selectedItemKey) return;

    const cloned_plates = structuredClone(stagedPlates);
    const item = cloned_plates[selectedPlateIndex].items.find(
      (item) => item.key === selectedItemKey
    );
    if (!item) return;

    const current_price = new Decimal(item.unit_price);
    const adjustment = new Decimal(add_value);
    const new_price = is_plus
      ? current_price.plus(adjustment)
      : current_price.minus(adjustment);

    item.unit_price = Math.max(0, new_price.toNumber());
    setStagedPlates(cloned_plates);
  }

  function handleQuantity(is_plus) {
    if (!selectedItemKey) return;

    const cloned_plates = structuredClone(stagedPlates);
    const item = cloned_plates[selectedPlateIndex].items.find(
      (item) => item.key === selectedItemKey
    );
    if (!item) return;

    const new_quantity = is_plus ? item.quantity + 1 : item.quantity - 1;
    item.quantity = Math.max(1, new_quantity);
    setStagedPlates(cloned_plates);
  }

  const onSubmit = () => {
    if (currentCategory.label === CATEGORY_TO_FILTER) {
      setIsModalOpen(true);
      return;
    }
    setPlates(structuredClone(stagedPlates));
    setSelectedItemKey(null);
    setSelectedSubCategory(null);
  };

  const sortItems = useCallback((items) => {
    if (!items) return [];
    return [...items].sort((a, b) => {
      if (a.sequence === null && b.sequence === null) return 0;
      if (a.sequence === null) return 1;
      if (b.sequence === null) return -1;
      return a.sequence - b.sequence;
    });
  }, []);

  const getSelectedItemQuantity = () => {
    if (!selectedItemKey || !stagedPlates[selectedPlateIndex]) return 0;
    const item = stagedPlates[selectedPlateIndex].items.find(
      (item) => item.key === selectedItemKey
    );
    return item?.quantity || 0;
  };

  return (
    <>
      <Row gutter={[8, 0]} style={{ height: "100%", width: "100%" }}>
        <Col span={24} style={{ height: "calc(15% - 4px)" }}>
          <div className="cat-container" style={{ height: "100%" }}>
            {sortItems(category).map((cat) => (
              <Button
                key={cat.id}
                className="cat-btn"
                style={{
                  borderColor:
                    currentCategory.label === cat.label ? "#4096ff" : "#d9d9d9",
                }}
                onClick={() => addOrUpdateItem(cat)}
              >
                <div
                  className={
                    currentCategory.label === cat.label
                      ? "select-cat-image-container"
                      : "cat-image-container"
                  }
                >
                  <img
                    className={
                      currentCategory.label === cat.label
                        ? "select-cat-image"
                        : "cat-image"
                    }
                    alt={cat.label}
                    src={cat.image}
                  />
                </div>
              </Button>
            ))}
          </div>
        </Col>
        <Col span={24} style={{ height: "calc(85% - 4px)" }}>
          <Row gutter={[8, 0]} style={{ height: "100%", width: "100%" }}>
            <Col span={16} style={{ height: "100%" }}>
              <div className="sub-cat-container">
                {sortItems(currentCategory.sub_categories).map((sub_cat) => (
                  <div
                    key={sub_cat.label}
                    className={
                      (
                        selectedSubCategory?.includes("+")
                          ? selectedSubCategory
                              .split("+")
                              .map((s) => s.trim())
                              .includes(sub_cat.label)
                          : selectedSubCategory === sub_cat.label
                      )
                        ? sub_cat.is_common
                          ? "selected-common-sub-cat-btn"
                          : "selected-sub-cat-btn"
                        : sub_cat.is_common
                          ? "common-sub-cat-btn"
                          : "sub-cat-btn"
                    }
                    onClick={() => onSubCatButtonClicked(sub_cat)}
                  >
                    <span className="cat-label">{sub_cat.label}</span>
                    <span className="unit-price">
                      RM {sub_cat.unit_price.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </Col>
            <Col span={8} style={{ height: "100%" }}>
              <Flex
                justify="space-around"
                style={{ width: "100%", marginBottom: "10px" }}
              >
                <span style={{ fontWeight: "bold", fontSize: "24px" }}>
                  {currentCategory.label}
                </span>
              </Flex>
              {selectedItemKey && (
                <div
                  style={{
                    width: "100%",
                    overflow: "auto",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Flex
                    justify="space-around"
                    style={{ width: "100%", marginBottom: "50px" }}
                  >
                    <Button
                      className="minus-btn"
                      icon={<MinusOutlined className="price-action-icon" />}
                      onClick={() => handleQuantity(false)}
                    />
                    <div
                      className="quantity-tag"
                      style={{
                        borderColor: isLightTheme ? "black" : "white",
                        background: isLightTheme ? "#e7e6e6" : "#e7e6e634",
                      }}
                    >
                      {getSelectedItemQuantity()} Qty
                    </div>
                    <Button
                      className="plus-btn"
                      icon={<PlusOutlined className="price-action-icon" />}
                      onClick={() => handleQuantity(true)}
                    />
                  </Flex>
                  {PRICE_SELECTION.map((price) => (
                    <Flex
                      key={price}
                      justify="space-around"
                      style={{ width: "100%", marginBottom: "5px" }}
                    >
                      <Button
                        className="minus-btn"
                        icon={<MinusOutlined className="price-action-icon" />}
                        onClick={() => handlePrice(price, false)}
                      />
                      <div
                        className="price-tag"
                        style={{
                          borderColor: isLightTheme ? "black" : "white",
                        }}
                      >
                        RM {price.toFixed(2)}
                      </div>
                      <Button
                        className="plus-btn"
                        icon={<PlusOutlined className="price-action-icon" />}
                        onClick={() => handlePrice(price, true)}
                      />
                    </Flex>
                  ))}
                  <Button
                    className="custom-btn"
                    style={{ width: "100%", marginTop: "50px" }}
                    type="primary"
                    onClick={onSubmit}
                    disabled={
                      !selectedItemKey ||
                      !selectedSubCategory ||
                      !currentCategory.sub_categories?.length
                    }
                    block
                  >
                    Confirm
                  </Button>
                </div>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      <SpecialCategoryModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        stagedPlates={stagedPlates}
        setStagedPlates={setStagedPlates}
        modalSubCategories={sortItems(currentCategory.modal_sub_categories)}
        selectedPlateIndex={selectedPlateIndex}
        selectedItemKey={selectedItemKey}
        selectedSubCategory={selectedSubCategory}
        setSelectedItemKey={setSelectedItemKey}
        setSelectedSubCategory={setSelectedSubCategory}
        setPlates={setPlates}
      />
    </>
  );
}
